import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';

/* Renders the assistant's reply as proper rich text: headings (bold, sized), paragraphs, bullet / numbered lists, quotes,
   tables, bold / italic / `inline code`, and fenced code blocks on a black background with light, colourful syntax colours.
   No markdown library — a small parser, so nothing is added to the bundle. `inline(text)` turns plain text into nodes
   (the chat uses it to turn module paths into link buttons). */

const GOLD_DARK = '#7a5712';
const GOLD = '#b8892c';

/* ── code highlighting (light colours for the black code box) ── */
const COLORS = {
  comment: '#7f8aa3', string: '#9be3a4', number: '#ffb86b', keyword: '#c4a7ff', fn: '#7dd3fc', ident: '#e6e9f2', punct: '#f4a6c8', plain: '#e6e9f2',
};
const KEYWORDS = new Set(('const let var function return if else for while do switch case break continue import from export default class extends new async await try catch finally throw ' +
  'true false null undefined this typeof instanceof in of void delete yield static super def lambda pass None True False and or not is as with elif raise global print ' +
  'public private protected interface type enum struct fn func package using namespace echo fi then done').split(' '));
const SQL_WORDS = new Set(('select from where insert into values update set delete join left right inner outer on group by order having limit offset as and or not null like in is distinct create table alter drop index primary key union all case when then else end count sum avg min max').split(' '));
const SQL_LANGS = ['sql', 'mysql', 'postgres', 'plsql'];

function commentPattern(lang) {
  const l = (lang || '').toLowerCase();
  if (['py', 'python', 'bash', 'sh', 'shell', 'zsh', 'yaml', 'yml', 'ruby', 'rb', 'powershell', 'ps1', 'dockerfile', 'env', 'ini', 'toml'].includes(l)) return '#[^\\n]*';
  if (SQL_LANGS.includes(l)) return '--[^\\n]*|\\/\\*[\\s\\S]*?\\*\\/';
  if (['html', 'xml', 'svg'].includes(l)) return '<!--[\\s\\S]*?-->';
  if (['css', 'scss', 'less'].includes(l)) return '\\/\\*[\\s\\S]*?\\*\\/';
  if (['json', 'text', 'txt', 'plaintext'].includes(l)) return '(?!)';
  return '\\/\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\/';
}

function highlight(code, lang) {
  const l = (lang || '').toLowerCase();
  const strings = '"(?:\\\\.|[^"\\\\\\n])*"|\'(?:\\\\.|[^\'\\\\\\n])*\'|`(?:\\\\.|[^`\\\\])*`';
  const re = new RegExp(
    `(${commentPattern(l)})|(${strings})|(\\b\\d+(?:\\.\\d+)?\\b)|([A-Za-z_$][\\w$-]*)(?=\\s*\\()|([A-Za-z_$][\\w$]*)|([{}()[\\];,.:=<>+\\-*/%!&|?^~]+)|([\\s\\S])`,
    'g',
  );
  const sql = SQL_LANGS.includes(l);
  const out = [];
  let m;
  let i = 0;
  while ((m = re.exec(code)) !== null) {
    const [tok, comment, str, num, fn, ident, punct] = m;
    let color = COLORS.plain;
    let fontStyle;
    if (comment) { color = COLORS.comment; fontStyle = 'italic'; }
    else if (str) color = COLORS.string;
    else if (num) color = COLORS.number;
    else if (fn) color = (sql ? SQL_WORDS.has(fn.toLowerCase()) : KEYWORDS.has(fn)) ? COLORS.keyword : COLORS.fn;
    else if (ident) color = (sql ? SQL_WORDS.has(ident.toLowerCase()) : KEYWORDS.has(ident)) ? COLORS.keyword : COLORS.ident;
    else if (punct) color = COLORS.punct;
    out.push(<span key={i++} style={{ color, fontStyle }}>{tok}</span>);
  }
  return out;
}

export function CodeBlock({ code, language }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(code).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {});
  };
  return (
    <Box sx={{ my: 1.2, borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.10)', background: '#0b0d12', boxShadow: '0 4px 14px rgba(0,0,0,0.25)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.4, py: 0.6, background: '#12151c', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography sx={{ fontSize: '0.66rem', color: '#9aa4bd !important', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{language || 'code'}</Typography>
        <Box component="button" onClick={handleCopy} sx={{ fontSize: '0.66rem', fontWeight: 700, color: copied ? '#34d399' : '#c3cbe0', background: 'none', border: 'none', cursor: 'pointer', '&:hover': { color: '#fff' } }}>
          {copied ? '✓ Copied' : 'Copy'}
        </Box>
      </Box>
      <Box component="pre" sx={{ m: 0, p: 1.6, background: '#0b0d12', fontSize: '0.78rem', fontFamily: '"Fira Code","Consolas",monospace', overflowX: 'auto', whiteSpace: 'pre', lineHeight: 1.6 }}>
        <code>{highlight(code, language)}</code>
      </Box>
    </Box>
  );
}

/* ── inline: **bold**, *italic*, `code` — everything else goes through inline() ── */
function Inline({ text, inline }) {
  const re = /(\*\*[^*\n]+?\*\*|__[^_\n]+?__|`[^`\n]+`|\*[^*\s][^*\n]*?\*)/g;
  const out = [];
  let last = 0;
  let m;
  let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(<React.Fragment key={k++}>{inline(text.slice(last, m.index))}</React.Fragment>);
    const t = m[0];
    if (t.startsWith('`')) {
      out.push(<Box key={k++} component="code" sx={{ px: '6px', py: '1px', borderRadius: '6px', background: '#14161c', color: '#ffd9a0', fontFamily: '"Fira Code","Consolas",monospace', fontSize: '0.8em' }}>{t.slice(1, -1)}</Box>);
    } else if (t.startsWith('**') || t.startsWith('__')) {
      out.push(<Box key={k++} component="strong" sx={{ fontWeight: 800, color: '#1d1a14' }}><Inline text={t.slice(2, -2)} inline={inline} /></Box>);
    } else {
      out.push(<Box key={k++} component="em" sx={{ fontStyle: 'italic' }}>{inline(t.slice(1, -1))}</Box>);
    }
    last = m.index + t.length;
  }
  if (last < text.length) out.push(<React.Fragment key={k++}>{inline(text.slice(last))}</React.Fragment>);
  return <>{out}</>;
}

const HEADING_SX = {
  1: { fontSize: '1.12rem', mt: 1.6, mb: 0.8, borderBottom: '2px solid rgba(184,137,44,0.35)', pb: 0.5 },
  2: { fontSize: '1.02rem', mt: 1.4, mb: 0.7, borderBottom: '1px solid rgba(184,137,44,0.28)', pb: 0.4 },
  3: { fontSize: '0.95rem', mt: 1.2, mb: 0.5 },
  4: { fontSize: '0.9rem', mt: 1, mb: 0.4 },
  5: { fontSize: '0.86rem', mt: 1, mb: 0.4 },
  6: { fontSize: '0.84rem', mt: 1, mb: 0.4 },
};

const TABLE_SEP = /^\s*\|?[\s:|-]+\|?\s*$/;
const LIST_ITEM = /^(\s*)([-*•]|\d+[.)])\s+(.*)$/;
const splitRow = (line) => line.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());

/* ── block parser ── */
function parseBlocks(text) {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  const isTableStart = (ln, next) => /^\s*\|/.test(ln) && next && TABLE_SEP.test(next) && next.includes('-');
  const special = (ln, next) => /^\s*#{1,6}\s+/.test(ln) || LIST_ITEM.test(ln) || /^\s*>/.test(ln)
    || /^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(ln) || isTableStart(ln, next);
  while (i < lines.length) {
    const ln = lines[i];
    if (!ln.trim()) { i++; continue; }
    let m = /^\s*(#{1,6})\s+(.*?)\s*#*\s*$/.exec(ln);
    if (m) { blocks.push({ t: 'h', level: m[1].length, text: m[2] }); i++; continue; }
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(ln)) { blocks.push({ t: 'hr' }); i++; continue; }
    if (isTableStart(ln, lines[i + 1])) {
      const head = splitRow(ln);
      const rows = [];
      i += 2;
      while (i < lines.length && /^\s*\|/.test(lines[i])) { rows.push(splitRow(lines[i])); i++; }
      blocks.push({ t: 'table', head, rows });
      continue;
    }
    if (/^\s*>/.test(ln)) {
      const q = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) { q.push(lines[i].replace(/^\s*>\s?/, '')); i++; }
      blocks.push({ t: 'quote', text: q.join('\n') });
      continue;
    }
    if (LIST_ITEM.test(ln)) {
      const items = [];
      while (i < lines.length) {
        const mm = LIST_ITEM.exec(lines[i]);
        if (!mm) break;
        items.push({ indent: Math.min(2, Math.floor(mm[1].replace(/\t/g, '  ').length / 2)), ordered: /\d/.test(mm[2]), text: mm[3] });
        i++;
      }
      blocks.push({ t: 'list', items });
      continue;
    }
    const para = [];
    while (i < lines.length && lines[i].trim() && !special(lines[i], lines[i + 1])) { para.push(lines[i]); i++; }
    if (!para.length) { para.push(ln); i++; }
    blocks.push({ t: 'p', text: para.join('\n') });
  }
  return blocks;
}

function Block({ b, inline }) {
  if (b.t === 'h') {
    return (
      <Typography component="div" sx={{ fontWeight: 800, color: `${GOLD_DARK} !important`, lineHeight: 1.35, ...HEADING_SX[b.level] }}>
        <Inline text={b.text} inline={inline} />
      </Typography>
    );
  }
  if (b.t === 'hr') return <Box sx={{ my: 1.2, height: '1px', background: 'rgba(184,137,44,0.35)' }} />;
  if (b.t === 'quote') {
    return (
      <Box sx={{ my: 1, pl: 1.4, py: 0.4, borderLeft: `3px solid ${GOLD}`, background: 'rgba(184,137,44,0.08)', borderRadius: '0 8px 8px 0', color: '#4a4234', whiteSpace: 'pre-wrap' }}>
        <Inline text={b.text} inline={inline} />
      </Box>
    );
  }
  if (b.t === 'table') {
    return (
      <Box sx={{ my: 1, overflowX: 'auto', borderRadius: '10px', border: '1px solid rgba(184,137,44,0.3)' }}>
        <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.82rem' }}>
          <thead>
            <tr>{b.head.map((c, k) => <Box key={k} component="th" sx={{ textAlign: 'left', px: 1.2, py: 0.7, background: 'rgba(184,137,44,0.16)', color: GOLD_DARK, fontWeight: 800, whiteSpace: 'nowrap' }}><Inline text={c} inline={inline} /></Box>)}</tr>
          </thead>
          <tbody>
            {b.rows.map((r, ri) => (
              <tr key={ri}>{r.map((c, k) => <Box key={k} component="td" sx={{ px: 1.2, py: 0.6, borderTop: '1px solid rgba(184,137,44,0.2)', verticalAlign: 'top' }}><Inline text={c} inline={inline} /></Box>)}</tr>
            ))}
          </tbody>
        </Box>
      </Box>
    );
  }
  if (b.t === 'list') {
    let n = 0;
    return (
      <Box sx={{ my: 0.7 }}>
        {b.items.map((it, k) => {
          if (it.ordered && it.indent === 0) n += 1;
          const marker = it.ordered && it.indent === 0 ? `${n}.` : (it.indent ? '◦' : '•');
          return (
            <Box key={k} sx={{ display: 'flex', gap: 0.9, alignItems: 'flex-start', ml: `${it.indent * 18}px`, mb: 0.45 }}>
              <Box component="span" sx={{ flexShrink: 0, minWidth: '1.4em', fontWeight: 800, color: GOLD, textAlign: 'right' }}>{marker}</Box>
              <Box sx={{ minWidth: 0, whiteSpace: 'pre-wrap' }}><Inline text={it.text} inline={inline} /></Box>
            </Box>
          );
        })}
      </Box>
    );
  }
  return <Box sx={{ my: 0.7, whiteSpace: 'pre-wrap' }}><Inline text={b.text} inline={inline} /></Box>;
}

/* the whole reply: text parts parsed as markdown, ```fenced``` parts as code blocks */
export default function MarkdownContent({ text, inline }) {
  const fence = /```([\w+#-]*)[^\S\n]*\n?([\s\S]*?)(?:```|$)/g;
  const nodes = [];
  const src = String(text || '');
  let last = 0;
  let m;
  let k = 0;
  const pushText = (t) => { parseBlocks(t).forEach((b) => nodes.push(<Block key={k++} b={b} inline={inline} />)); };
  while ((m = fence.exec(src)) !== null) {
    if (m.index > last) pushText(src.slice(last, m.index));
    nodes.push(<CodeBlock key={k++} language={m[1]} code={m[2].replace(/\n$/, '')} />);
    last = fence.lastIndex;
    if (m[0] === '') fence.lastIndex += 1;
  }
  if (last < src.length) pushText(src.slice(last));
  return <Box sx={{ '& > *:first-of-type': { mt: 0 }, '& > *:last-child': { mb: 0 } }}>{nodes}</Box>;
}
